export default {
  slug: "/zircon-teeth",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/zirconium.webp",

  en: {
    title: "Zirconia Crowns in Istanbul",
    eyebrow: "Dental care",
    lead:
      "A zirconia crown is milled from a block of zirconium dioxide and sintered until it is hard. It is used " +
      "where a metal-ceramic crown would show a grey line at the gum, and it is strong enough for the back " +
      "teeth as well as the front.",
    cta: "Book a free assessment",
    heroAlt: "Zirconia crown consultation at Platinya Clinic",
    stats: [
      { value: "2 visits", label: "At the clinic" },
      { value: "5–7 days", label: "Laboratory time" },
      { value: "10–15 years", label: "Typical lifespan" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "Zirconia is chosen for strength, for aesthetics without metal, or for both. It is not automatically " +
        "better than a well-made metal-ceramic crown — it is different.",
      items: [
        "Heavily filled, root-treated or cracked teeth that need full coverage",
        "Back teeth under heavy load, where a thin all-ceramic crown would chip",
        "Visible front teeth where the gum line has to stay free of metal",
        "Known metal sensitivity, or an existing metal-ceramic crown with a dark margin showing",
      ],
      note:
        "A crown needs enough tooth to hold on to. If very little is left above the gum, the tooth may need a " +
        "core build-up or crown lengthening first; a crown cannot replace missing tooth structure " +
        "indefinitely.",
    },
    techniques: {
      heading: "Monolithic or layered",
      intro:
        "Both are zirconia. The difference is whether the whole crown is the same material or a strong core " +
        "with porcelain on top, and that decides how it looks and how it fails.",
      items: [
        {
          name: "Monolithic zirconia",
          text:
            "The entire crown is milled zirconia, sintered and polished. The strongest option for molars and " +
            "for anyone who grinds; less translucent than layered ceramic, so on a front tooth it can read " +
            "flat unless the shade and surface texture are handled carefully.",
        },
        {
          name: "Layered zirconia",
          text:
            "A zirconia core with porcelain built over it. Better at the front, where light has to pass " +
            "through the tooth, but the veneering porcelain can chip — the usual failure of this design. A " +
            "reasonable compromise on premolars and visible teeth.",
        },
        {
          name: "Metal-ceramic, for comparison",
          text:
            "A metal coping with porcelain fused on top: predictable, cheaper and very well proven. Its " +
            "weakness is aesthetic — an opaque core, and over the years a grey line where the gum recedes " +
            "away from the metal margin.",
        },
      ],
    },
    journey: {
      heading: "From preparation to cemented crown",
      steps: [
        {
          name: "Assessment",
          text:
            "Why the tooth needs a crown is established first: a crack, a large old filling, root canal " +
            "treatment. How much tooth remains above the gum decides whether a crown is enough.",
        },
        {
          name: "Preparation",
          text:
            "Under local anaesthetic the tooth is reduced by 1–1.5 mm, more where porcelain will be layered " +
            "on. A scan or impression is taken and a temporary crown fitted so the tooth is protected and " +
            "does not move.",
        },
        {
          name: "Design and milling",
          text:
            "The crown is designed in CAD, milled from the zirconia blank and sintered at high temperature, " +
            "then stained and glazed. This is where the shade is set — a shade match taken under surgery " +
            "light will look wrong in daylight.",
        },
        {
          name: "Try-in and cementation",
          text:
            "Fit, contact points and shade are checked before cementing. The crown is then seated with resin " +
            "or glass-ionomer cement and the bite is refined until the contacts feel even.",
        },
        {
          name: "Review",
          text:
            "A check at one to two weeks, mainly for the bite. If there are signs of grinding, a night guard " +
            "is made — a crown protects a tooth, it does not protect itself from a heavy bite.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "Zirconia is strong, but a crown is a restoration on a tooth that has already failed once. These are " +
        "the realistic problems.",
      items: [
        "The crown can come off if the core is short or the cement is compromised — usually re-cementable",
        "Chipping of the layered porcelain, most often on a thin crown or one carrying a heavy bite",
        "Zirconia is harder than enamel and can wear the opposing tooth over years, especially if the surface is left unpolished",
        "A monolithic crown can look opaque or too uniform at the front, particularly in bright light",
        "Pulp inflammation, or the need for root canal treatment, after preparation of a tooth that was already compromised",
      ],
      note:
        "A crown is only as good as what is underneath it. If the tooth is cracked below the gum or has very " +
        "little structure left, the honest option may be an extraction and an implant rather than a third " +
        "crown on the same tooth.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Is it better than metal-ceramic?",
          a:
            "At the gum line, yes: there is no metal collar, so no grey line appears as the gum recedes. For " +
            "colour and translucency, a well-layered ceramic can still look better. Monolithic zirconia wins " +
            "on strength, not on realism.",
        },
        {
          q: "Does zirconia break?",
          a:
            "A monolithic crown rarely breaks as a unit — it is the strongest ceramic crown in routine use. " +
            "The common failure is the porcelain layer on a layered crown chipping, which is repairable but " +
            "not invisible.",
        },
        {
          q: "Can it be used on back teeth?",
          a:
            "Yes — that is its main advantage. Molars take the heaviest load, and zirconia handles it better " +
            "than glass-ceramic. For a second molar, a monolithic crown is usually the sensible choice.",
        },
        {
          q: "How long will it last?",
          a:
            "Ten to fifteen years is a fair expectation, sometimes longer. The limit is rarely the zirconia " +
            "itself: it is the tooth underneath, the gum margin and whether the bite is controlled.",
        },
      ],
    },
    ctaBand: {
      heading: "Send a photograph of the tooth",
      text:
        "A close photograph and an x-ray of the tooth are enough to say whether a zirconia crown, an onlay or " +
        "an extraction is the more sensible repair — and why.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the tooth, " +
      "the gums and the bite, and reviewing the medical history; results vary between patients.",
  },

  ar: {
    title: "تركيبات الزيركون في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "تاج الزيركون يُحفر من كتلة من ثاني أكسيد الزركونيوم ثم يُلبَّد حتى يصبح صلباً. ويُستخدم حيث تُظهر " +
      "التركيبات المعدنية الخزفية خطاً رمادياً عند اللثة، وقوّته تكفي الأضراس الخلفية كما تكفي الأسنان " +
      "الأمامية.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "استشارة تركيبات الزيركون في عيادة بلاتينيا",
    stats: [
      { value: "زيارتان", label: "في العيادة" },
      { value: "٥–٧ أيام", label: "مدة عمل المختبر" },
      { value: "١٠–١٥ سنة", label: "العمر المعتاد" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "يُختار الزيركون للقوة، أو للمظهر دون معدن، أو لكليهما. وهو ليس أفضل تلقائياً من تلبيسة معدنية " +
        "خزفية جيدة الصنع — إنه مختلف.",
      items: [
        "أسنان كثيرة الحشوات أو معالَجة العصب أو متشقّقة تحتاج تغطية كاملة",
        "أضراس خلفية تحت حِمل ثقيل، حيث تنكسر التلبيسة الخزفية الرقيقة",
        "أسنان أمامية ظاهرة يجب أن يبقى خط لثتها خالياً من المعدن",
        "حساسية معروفة للمعادن، أو تلبيسة معدنية خزفية قائمة تظهر حافتها الداكنة",
      ],
      note:
        "تحتاج التلبيسة إلى ما يكفي من السن لتثبت عليه. وإذا لم يبقَ فوق اللثة إلا القليل، فقد يحتاج السن " +
        "إلى بناء دعامة أو تطويل تاج أولاً؛ فالتلبيسة لا تعوّض بنية السن المفقودة إلى ما لا نهاية.",
    },
    techniques: {
      heading: "زيركون كامل أم طبقات",
      intro:
        "كلاهما زيركون. والفرق في أن التلبيسة كلها من المادة نفسها، أم أنها قلب قوي وعليه خزف؛ وهذا يحدّد " +
        "المظهر وطريقة الفشل.",
      items: [
        {
          name: "الزيركون الكامل",
          text:
            "التلبيسة كلها زيركون يُحفر ثم يُلبَّد ويُلمَّع. وهو الخيار الأقوى للأضراس ولمن يعاني صرير الأسنان؛ " +
            "لكنه أقل شفافية من الخزف الطبقي، فقد يبدو مسطّحاً على سن أمامي إن لم يُعالَج اللون ونسيج السطح " +
            "بعناية.",
        },
        {
          name: "الزيركون الطبقي",
          text:
            "قلب من الزيركون يُبنى عليه الخزف. أفضل في المقدمة حيث يجب أن ينفذ الضوء عبر السن، لكن خزف " +
            "التغليف قد يتكسّر — وهو الفشل المعتاد في هذا التصميم. حلّ وسط معقول للضواحك والأسنان الظاهرة.",
        },
        {
          name: "المعدني الخزفي، للمقارنة",
          text:
            "قاعدة معدنية يُدمج عليها الخزف: نتيجة متوقّعة وأرخص وسابقة طويلة موثوقة. وضعفه في المظهر — قلب " +
            "معتم، ومع السنوات خط رمادي حيث تنحسر اللثة عن الحافة المعدنية.",
        },
      ],
    },
    journey: {
      heading: "من التحضير إلى تثبيت التاج",
      steps: [
        {
          name: "التقييم",
          text:
            "يُحدَّد أولاً سبب الحاجة إلى التاج: شقّ أو حشوة قديمة كبيرة أو علاج عصب. ومقدار ما تبقّى من " +
            "السن فوق اللثة يقرّر هل تكفي التلبيسة.",
        },
        {
          name: "التحضير",
          text:
            "تحت تخدير موضعي يُبرد السن ١ إلى ١٫٥ ملم، وأكثر حيث سيُبنى خزف طبقي. ويُؤخذ مسح أو طبعة " +
            "وتُركَّب تلبيسة مؤقتة لحماية السن ومنع تحرّكه.",
        },
        {
          name: "التصميم والحفر",
          text:
            "يُصمَّم التاج بالحاسوب ويُحفر من كتلة الزيركون ويُلبَّد في حرارة عالية، ثم يُلوَّن ويُطلى. وهنا " +
            "يُحدَّد اللون — ولون يُؤخذ تحت ضوء العيادة سيبدو خاطئاً في ضوء النهار.",
        },
        {
          name: "التجربة والتثبيت",
          text:
            "تُفحص الملاءمة ونقاط التلامس واللون قبل الإسمنت. ثم يُثبَّت التاج بإسمنت راتنجي أو زجاجي " +
            "أيوني ويُضبط الإطباق حتى تصبح التلامسات متساوية.",
        },
        {
          name: "المراجعة",
          text:
            "فحص بعد أسبوع أو أسبوعين، أساسه الإطباق. وإذا ظهرت علامات صرير يُصنع واقٍ ليلي — فالتاج يحمي " +
            "السن ولا يحمي نفسه من إطباق ثقيل.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "الزيركون قوي، لكن التاج ترميم على سن فشل سابقاً. وهذه المشاكل الواقعية.",
      items: [
        "انفصال التاج إذا كانت الدعامة قصيرة أو أضعفها الإسمنت — وهو قابل لإعادة التثبيت عادةً",
        "تكسّر الخزف الطبقي، وأكثر ما يحدث في تاج رقيق أو تحت إطباق ثقيل",
        "الزيركون أصلب من المينا وقد يبري السن المقابل على مدى سنوات، خصوصاً إذا تُرك السطح دون تلميع",
        "قد يبدو التاج الكامل معتماً أو موحّداً أكثر من اللازم في المقدمة، خاصةً في الضوء القوي",
        "التهاب اللبّ أو الحاجة إلى علاج عصب بعد تحضير سن كان متضرراً أصلاً",
      ],
      note:
        "قيمة التاج بما تحته. وإذا كان السن متشقّقاً تحت اللثة أو لم يبقَ منه إلا القليل، فقد يكون الخيار " +
        "الصادق قلع السن وزراعته بدل تلبيسة ثالثة على السن نفسه.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل هو أفضل من المعدني الخزفي؟",
          a:
            "عند خط اللثة نعم: لا حافة معدنية فلا يظهر خط رمادي مع انحسار اللثة. أما اللون والشفافية فقد " +
            "يبقى الخزف الطبقي الجيد أفضل. والزيركون الكامل يتفوّق في القوة لا في الواقعية.",
        },
        {
          q: "هل يتكسّر الزيركون؟",
          a:
            "التاج الكامل نادراً ما يتكسّر كوحدة — وهو أقوى تلبيسة خزفية شائعة الاستخدام. والفشل الشائع " +
            "هو تكسّر طبقة الخزف في التاج الطبقي، وهو قابل للإصلاح لكنه لا يعود غير مرئي.",
        },
        {
          q: "هل يُستخدم للأضراس الخلفية؟",
          a:
            "نعم — وهذه ميزته الأساسية. الأضراس تتحمّل أكبر حِمل، والزيركون يصمد فيها أكثر من الخزف " +
            "الزجاجي. وللضرس الثاني غالباً يكون التاج الكامل هو الاختيار المنطقي.",
        },
        {
          q: "كم يدوم؟",
          a:
            "عشر إلى خمس عشرة سنة توقّع معقول، وأحياناً أكثر. والحدّ نادراً ما يكون الزيركون نفسه: بل السن " +
            "تحته وحافة اللثة ومدى ضبط الإطباق.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورة للسن",
      text:
        "صورة قريبة وأشعة للسن تكفيان لنقول هل تلبيسة الزيركون أم الحشوة الجزئية أم القلع هو الإصلاح " +
        "الأسلم — ولماذا.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص السن واللثة والإطباق " +
      "ومراجعة التاريخ المرضي؛ والنتائج تختلف من مريض لآخر.",
  },
};
